import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { vblApi } from '@/services/api/vblApi';
import { VblAnalysisResult, VblRecord, VBL_ANALYSIS_METHOD } from '@/types/vbl';
import { FailureFilters, DEFAULT_FAILURE_FILTERS } from '@/types';
import { calculateVblBoundaries } from '@/utils/vblBoundaries';

/**
 * Configuration constants for VBL analysis
 */
const VBL_ANALYSIS_CONFIG = {
  /** Minimum number of records required for analysis */
  MIN_SAMPLE_SIZE: 2,
  /** Default confidence level for parameter intervals */
  DEFAULT_CONFIDENCE_LEVEL: 0.95,
  /** Parameter bounds for VBL data in days */
  PARAMETER_BOUNDS: {
    SHAPE: { min: 0.1, max: 10 },
    SCALE: { min: 1, max: 10000 },
  },
  /** Plotting position parameters (Benard's approximation) */
  PLOTTING: {
    ALPHA: 0.3,
    BETA: 0.4,
  },
  /** Query cache configuration */
  CACHE: {
    STALE_TIME: 5 * 60 * 1000, // 5 minutes
    CACHE_TIME: 10 * 60 * 1000, // 10 minutes
  },
} as const;

/**
 * Custom error class for VBL analysis failures
 */
class VblAnalysisError extends Error {
  constructor(message: string, public context?: Record<string, unknown>) {
    super(message);
    this.name = 'VblAnalysisError';
  }
}

/**
 * Filters VBL records to include only valid failures with complete data.
 * @param vblRecords - Array of VBL records to filter
 * @returns Array of valid VBL records
 */
function filterValidVblRecords(vblRecords: VblRecord[]): VblRecord[] {
  return vblRecords.filter(vbl =>
    vbl.degradation_time > 0 &&
    vbl.down_date &&
    vbl.detected_date
  );
}

/**
 * Calculates Weibull parameters for VBL data using Johnson's method.
 * VBL data is never censored since it only includes failures with degradation times.
 *
 * @param vblRecords - Array of VBL failure records
 * @param confidenceLevel - Confidence level for parameter intervals (default: 0.95)
 * @returns Weibull analysis result or null if insufficient data
 */
export function calculateJohnsonVbl(vblRecords: VblRecord[], confidenceLevel: number = VBL_ANALYSIS_CONFIG.DEFAULT_CONFIDENCE_LEVEL): VblAnalysisResult | null {
  if (vblRecords.length < VBL_ANALYSIS_CONFIG.MIN_SAMPLE_SIZE) {
    return null;
  }

  // Create paired data points that maintain the association between times and records
  const dataPoints = vblRecords
    .filter(vbl => vbl.degradation_time > 0)
    .map(vbl => ({
      time: vbl.degradation_time,
      record: vbl
    }));

  // Sort the paired data by degradation time
  const sortedData = dataPoints.sort((a, b) => a.time - b.time);

  // Extract sorted times for calculations
  const degradationTimes = sortedData.map(d => d.time);
  const sortedVblRecords = sortedData.map(d => d.record);

  if (degradationTimes.length < VBL_ANALYSIS_CONFIG.MIN_SAMPLE_SIZE) {
    return null;
  }

  const n = degradationTimes.length;

  // Calculate plotting positions using Benard's approximation
  const plottingPositions = degradationTimes.map((_, i) => {
    return (i + VBL_ANALYSIS_CONFIG.PLOTTING.ALPHA) / (n + VBL_ANALYSIS_CONFIG.PLOTTING.BETA);
  });

  // Transform for linear regression
  const x = degradationTimes.map(t => Math.log(t));
  const y = plottingPositions.map(p => Math.log(-Math.log(1 - p)));

  // Linear regression
  const sumX = x.reduce((a, b) => a + b, 0);
  const sumY = y.reduce((a, b) => a + b, 0);
  const sumXY = x.reduce((sum, xi, i) => sum + xi * y[i], 0);
  const sumX2 = x.reduce((sum, xi) => sum + xi * xi, 0);

  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;

  // Weibull parameters with bounds checking for day-scale VBL data
  const rawShape = slope;
  const rawScale = Math.exp(-intercept / slope);
  const shape = Math.max(
    VBL_ANALYSIS_CONFIG.PARAMETER_BOUNDS.SHAPE.min,
    Math.min(VBL_ANALYSIS_CONFIG.PARAMETER_BOUNDS.SHAPE.max, rawShape)
  );
  const scale = Math.max(
    VBL_ANALYSIS_CONFIG.PARAMETER_BOUNDS.SCALE.min,
    Math.min(VBL_ANALYSIS_CONFIG.PARAMETER_BOUNDS.SCALE.max, rawScale)
  );

  // Calculate R²
  const yMean = sumY / n;
  const ssTotal = y.reduce((sum, yi) => sum + Math.pow(yi - yMean, 2), 0);
  const ssResidual = y.reduce((sum, yi, i) => {
    const yPred = intercept + slope * x[i];
    return sum + Math.pow(yi - yPred, 2);
  }, 0);
  const rSquared = 1 - (ssResidual / ssTotal);

  // Calculate separate standard errors for shape and scale parameters
  const residualVariance = ssResidual / (n - 2);
  const xMean = x.reduce((s, v) => s + v, 0) / n;
  const sxx = x.reduce((sum, xi) => sum + Math.pow(xi - xMean, 2), 0);

  // Standard error for shape parameter
  const seShape = Math.sqrt(residualVariance / sxx);

  // Standard error for log scale parameter (using Delta method)
  const seLogScale = Math.sqrt(residualVariance * (1 / n + (xMean * xMean) / sxx));
  // Note: seScale = scale * seLogScale (kept for reference but not used directly)

  const tValue = confidenceLevel === 0.99 ? 2.576 : confidenceLevel === 0.90 ? 1.645 : 1.96;

  // Parameter confidence intervals using proper standard errors
  const shapeCI = {
    lower: Math.max(0.1, shape - tValue * seShape),
    upper: shape + tValue * seShape
  };

  const scaleCI = {
    lower: scale * Math.exp(-tValue * seLogScale),
    upper: scale * Math.exp(tValue * seLogScale)
  };

  // Calculate summary statistics
  const gamma = (x: number) => {
    // Approximation for gamma function
    const g = 7;
    const coef = [0.99999999999980993, 676.5203681218851, -1259.1392167224028,
                  771.32342877765313, -176.61502916214059, 12.507343278686905,
                  -0.13857109526572012, 9.9843695780195716e-6, 1.5056327351493116e-7];
    
    if (x < 0.5) {
      return Math.PI / (Math.sin(Math.PI * x) * gamma(1 - x));
    }
    
    x -= 1;
    let a = coef[0];
    for (let i = 1; i < g + 2; i++) {
      a += coef[i] / (x + i);
    }
    
    const t = x + g + 0.5;
    return Math.sqrt(2 * Math.PI) * Math.pow(t, x + 0.5) * Math.exp(-t) * a;
  };

  const meanLife = scale * gamma(1 + 1 / shape);
  const medianLife = scale * Math.pow(Math.log(2), 1 / shape);

  // Generate plot data with all VblRecord fields using correct naming
  // Now using sortedVblRecords which maintains proper association with sorted degradation times
  const empirical = degradationTimes.map((time, i) => ({
    x: time,
    y: plottingPositions[i],
    time,
    probability: plottingPositions[i],
    // Core identifiers
    failure_id: sortedVblRecords[i].failure_id,
    case_id: sortedVblRecords[i].case_id.toString(),
    // Turbine information
    turbine_id: sortedVblRecords[i].turbine_id,
    turbine_name: sortedVblRecords[i].turbine_name,
    model_id: sortedVblRecords[i].model_id,
    model_name: sortedVblRecords[i].model_name,
    // Site information
    site_id: sortedVblRecords[i].site_id,
    site_name: sortedVblRecords[i].site_name,
    // Component information
    component_id: sortedVblRecords[i].component_id,
    component_name: sortedVblRecords[i].component_name,
    major_component_id: sortedVblRecords[i].major_component_id,
    major_component_name: sortedVblRecords[i].major_component_name,
    component_major: sortedVblRecords[i].component_major,
    component_minor: sortedVblRecords[i].component_minor,
    // Failure information
    failure_mode_name: sortedVblRecords[i].failure_mode_name,
    failure_mode: sortedVblRecords[i].failure_mode,
    // Outcome information
    outcome_id: sortedVblRecords[i].outcome_id,
    outcome_name: sortedVblRecords[i].outcome_name,
    // Date information
    detected_date: sortedVblRecords[i].detected_date,
    down_date: sortedVblRecords[i].down_date,
    actual_rts_date: sortedVblRecords[i].actual_rts_date,
    predicted_rts_date: sortedVblRecords[i].predicted_rts_date,
    install_date: sortedVblRecords[i].install_date,
    cod_date: sortedVblRecords[i].cod_date,
    failure_date: sortedVblRecords[i].failure_date,
    // Metadata
    created_at: sortedVblRecords[i].created_at,
    updated_at: sortedVblRecords[i].updated_at,
    // Computed fields
    degradation_time_years: sortedVblRecords[i].degradation_time_years,
  }));

  // Use shared boundary calculations for consistency with chart
  const boundaries = calculateVblBoundaries(degradationTimes);
  const minTime = boundaries.curveMin;
  const maxTime = boundaries.curveMax;


  const theoretical = Array.from({ length: 100 }, (_, i) => {
    const time = minTime + (maxTime - minTime) * (i / 99);
    const probability = 1 - Math.exp(-Math.pow(time / scale, shape));
    return { x: time, y: probability, time, probability };
  });


  // Enhanced confidence bands with better bounds checking
  const confidenceIntervals = {
    lower: theoretical.map(point => {
      const prob = Math.max(0.0001, 1 - Math.exp(-Math.pow(point.x / scaleCI.upper, shapeCI.lower)));
      return {
        x: point.x,
        y: Math.min(0.9999, Math.max(0.0001, prob)) // Ensure valid probability bounds
      };
    }),
    upper: theoretical.map(point => {
      const prob = Math.min(0.9999, 1 - Math.exp(-Math.pow(point.x / scaleCI.lower, shapeCI.upper)));
      return {
        x: point.x,
        y: Math.min(0.9999, Math.max(0.0001, prob)) // Ensure valid probability bounds
      };
    })
  };

  return {
    parameters: {
      shape,
      scale,
      shapeCI,
      scaleCI,
      rSquared
    },
    statistics: {
      standardErrors: {
        shape: seShape,
        scale: seLogScale // Keep as log-scale SE for proper uncertainty propagation
      }
    },
    summary: {
      sampleSize: n,
      meanLife,
      medianLife,
      characteristicLife: scale
    },
    plotData: {
      empirical,
      theoretical,
      confidenceIntervals
    },
    metadata: {
      method: VBL_ANALYSIS_METHOD
    }
  };
}

/**
 * Hook for VBL (Vibration-Based Life) analysis using Johnson's method.
 *
 * Analyzes degradation time (detection to failure) instead of operation time.
 * VBL data is never censored since it only includes confirmed failures.
 *
 * @param filters - Failure filters to apply to the dataset
 * @param confidenceLevel - Confidence level for parameter intervals (0-1)
 * @returns Analysis results including parameters, metadata, and diversity metrics
 *
 * @throws {VblAnalysisError} When analysis calculation fails
 *
 * @example
 * ```typescript
 * const { analysis, isLoading, hasEnoughData } = useVblAnalysis(
 *   { componentsMajor: ['Gearbox'] },
 *   0.95
 * );
 * ```
 */
export function useVblAnalysis(
  filters: FailureFilters = DEFAULT_FAILURE_FILTERS,
  confidenceLevel: number = VBL_ANALYSIS_CONFIG.DEFAULT_CONFIDENCE_LEVEL
) {
  
  // Query for VBL data
  const { data: vblData, isLoading, error } = useQuery({
    queryKey: ['vbl', 'analysis', filters],
    queryFn: () => vblApi.getVblForWeibullAnalysis(filters),
    staleTime: VBL_ANALYSIS_CONFIG.CACHE.STALE_TIME,
    cacheTime: VBL_ANALYSIS_CONFIG.CACHE.CACHE_TIME,
  });
  

  // Perform analysis when data is available
  const analysis = useMemo(() => {
    if (!vblData?.vbls || vblData.vbls.length < VBL_ANALYSIS_CONFIG.MIN_SAMPLE_SIZE) {
      return null;
    }

    const validVbls = filterValidVblRecords(vblData.vbls);

    if (validVbls.length < VBL_ANALYSIS_CONFIG.MIN_SAMPLE_SIZE) {
      return null;
    }

    // Perform analysis using Johnson's method (VBL data is never censored)
    try {
      return calculateJohnsonVbl(validVbls, confidenceLevel);
    } catch (err) {
      throw new VblAnalysisError(
        'Johnson method calculation failed',
        {
          sampleSize: validVbls.length,
          confidenceLevel,
          originalError: err instanceof Error ? err.message : String(err),
        }
      );
    }
  }, [vblData, confidenceLevel]);

  // Calculate metadata
  const metadata = useMemo(() => {
    if (!vblData?.vbls) return null;

    const validVbls = filterValidVblRecords(vblData.vbls);

    return {
      totalSamples: validVbls.length,
      failureCount: validVbls.length,
      censoredCount: 0, // VBL data never contains censored observations
      hasCensoredData: false,
      degradationTimeRange: {
        min: Math.min(...validVbls.map(v => v.degradation_time)),
        max: Math.max(...validVbls.map(v => v.degradation_time))
      },
      sites: Array.from(new Set(validVbls.map(v => v.site_name))),
      components: Array.from(new Set(validVbls.map(v => v.major_component_name)))
    };
  }, [vblData]);

  // Calculate diversity metrics
  const diversityMetrics = useMemo(() => {
    if (!vblData?.vbls) return null;

    const validVbls = filterValidVblRecords(vblData.vbls);

    return {
      uniqueSites: new Set(validVbls.map(v => v.site_name)).size,
      uniqueTurbines: new Set(validVbls.map(v => v.turbine_name)).size,
      uniqueComponents: new Set(validVbls.map(v => v.major_component_name)).size,
      uniqueFailureModes: new Set(validVbls.map(v => v.failure_mode)).size
    };
  }, [vblData]);

  return {
    analysis,
    isLoading,
    error,
    hasEnoughData: (vblData?.vbls?.length || 0) >= 2,
    sampleSize: vblData?.vbls?.length || 0,
    metadata,
    diversityMetrics
  };
}

